import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import {
  CollaboratorListingsDTO,
  ListingDTO,
  UserListingDTO,
} from "@shaarp/common";
import {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type ListingsContextType = {
  listings: ListingDTO[];
  userListings: ListingDTO[];
  shaarpListings: ListingDTO[];
  assignedListings: UserListingDTO[];
  collaboratorsListings: CollaboratorListingsDTO[];
  apiFetchListings: (options?: { force?: boolean }) => Promise<void>;
  apiFetchAssignedListings: (options?: {
    force?: boolean;
  }) => Promise<void>;
  apiFetchCollaboratorsListings: (options?: {
    force?: boolean;
  }) => Promise<void>;
  apiFetchLoading: boolean;
  /** Listings whose items are still being written — their count is not final yet. */
  populatingListingsIds: ReadonlySet<string>;
  trackListingPopulation: <T>(
    listingId: string,
    run: () => Promise<T>,
  ) => Promise<T>;
};

const ListingsContext = createContext<ListingsContextType>({
  listings: [],
  userListings: [],
  shaarpListings: [],
  assignedListings: [],
  collaboratorsListings: [],
  apiFetchListings: async () => {},
  apiFetchAssignedListings: async () => {},
  apiFetchCollaboratorsListings: async () => {},
  apiFetchLoading: false,
  populatingListingsIds: new Set<string>(),
  trackListingPopulation: async (_listingId, run) => run(),
});

export const useListings = (): ListingsContextType => {
  const context = useContext(ListingsContext);
  if (!context) {
    throw new Error("useListings must be used within an ListingsProvider");
  }
  return context;
};

/**
 * Coalesces concurrent callers onto a single in-flight promise stored in `ref`.
 * Subsequent callers await the same promise; the ref is cleared once it settles.
 */
function dedupeAsync(
  ref: MutableRefObject<Promise<void> | null>,
  run: () => Promise<void>,
): Promise<void> {
  if (ref.current) {
    return ref.current;
  }
  ref.current = run().finally(() => {
    ref.current = null;
  });
  return ref.current;
}

export const ListingsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const toastError = useToastError();
  const api = useApi();
  const arePeopleVisible = useArePeopleVisible();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [listings, setListings] = useState<ListingDTO[]>([]);
  const [assignedListings, setAssignedListings] = useState<UserListingDTO[]>(
    [],
  );
  const [collaboratorsListings, setCollaboratorsListings] = useState<
    CollaboratorListingsDTO[]
  >([]);

  const [populatingListingsIds, setPopulatingListingsIds] = useState<
    ReadonlySet<string>
  >(new Set());

  const fetchListingsPromiseRef = useRef<Promise<void> | null>(null);
  const fetchAssignedListingsPromiseRef = useRef<Promise<void> | null>(null);
  const fetchCollaboratorsListingsPromiseRef = useRef<Promise<void> | null>(
    null,
  );

  const fetchListings = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!force && listings.length) {
        return;
      }
      await dedupeAsync(fetchListingsPromiseRef, async () => {
        try {
          setIsLoading(true);
          const { data } = await api.get<ListingDTO[]>(`/listings`);
          setListings(data);
        } catch {
          toastError();
        } finally {
          setIsLoading(false);
        }
      });
    },
    [api, toastError, listings.length],
  );

  const fetchAssignedListings = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!force && assignedListings.length) {
        return;
      }
      await dedupeAsync(fetchAssignedListingsPromiseRef, async () => {
        try {
          const { data } = await api.get<UserListingDTO[]>(
            `/listing/assignations/listings`,
          );
          setAssignedListings(data);
        } catch {
          toastError();
        }
      });
    },
    [api, toastError, assignedListings.length],
  );

  const fetchCollaboratorsListings = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!force && collaboratorsListings.length) {
        return;
      }
      await dedupeAsync(fetchCollaboratorsListingsPromiseRef, async () => {
        try {
          const { data } = await api.get<CollaboratorListingsDTO[]>(
            `/listings/team-members`,
          );
          setCollaboratorsListings(data);
        } catch {
          toastError();
        }
      });
    },
    [api, toastError, collaboratorsListings.length],
  );

  /**
   * Flags a listing as being filled for the whole duration of `run`, so the
   * lists that display it can hold back a count that is still incomplete — a
   * list created from the chat is saved before its companies are resolved.
   * Lives here rather than in the caller, which may unmount (a chat table gets
   * re-rendered) long before the population it started has finished.
   */
  const trackListingPopulation = useCallback(
    async <T,>(listingId: string, run: () => Promise<T>): Promise<T> => {
      setPopulatingListingsIds((ids) => new Set(ids).add(listingId));
      try {
        return await run();
      } finally {
        setPopulatingListingsIds((ids) => {
          const next = new Set(ids);
          next.delete(listingId);
          return next;
        });
      }
    },
    [],
  );

  // A list is filled from this tab and nowhere else: closing or reloading it
  // mid-way drops the companies not found yet, so the browser asks first.
  const isPopulating = populatingListingsIds.size > 0;
  useEffect(() => {
    if (!isPopulating) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isPopulating]);

  const value = useMemo(() => {
    // A team whose contact switch is off sees no key contacts (#757): its contact
    // lists are left out of every list, the ones shared with it included.
    const isShown = (listing: { listingType: string }): boolean =>
      arePeopleVisible || listing.listingType !== "employees";
    const shownListings = listings.filter(isShown);
    return {
      listings: shownListings,
      userListings: shownListings.filter(
        (listing) => listing.ownerType === "user",
      ),
      shaarpListings: shownListings.filter(
        (listing) => listing.ownerType === "shaarp",
      ),
      assignedListings: assignedListings.filter(isShown),
      collaboratorsListings: arePeopleVisible
        ? collaboratorsListings
        : collaboratorsListings.map((collaborator) => ({
            ...collaborator,
            listings: collaborator.listings.filter(isShown),
          })),
      apiFetchListings: fetchListings,
      apiFetchAssignedListings: fetchAssignedListings,
      apiFetchCollaboratorsListings: fetchCollaboratorsListings,
      apiFetchLoading: isLoading,
      populatingListingsIds,
      trackListingPopulation,
    };
  }, [
    arePeopleVisible,
    listings,
    assignedListings,
    collaboratorsListings,
    fetchListings,
    fetchAssignedListings,
    fetchCollaboratorsListings,
    isLoading,
    populatingListingsIds,
    trackListingPopulation,
  ]);

  return (
    <ListingsContext.Provider value={value}>
      {children}
    </ListingsContext.Provider>
  );
};
